import {
  MAX_CLASSIFICATION_ID,
  PointCloudClassDefinition,
  PointCloudStyling,
  PointCloudStylingClassification,
  PointCloudStylingClearance,
  PointCloudStylingElevation,
  PointCloudStylingEyeDomeLighting,
  PointCloudStylingIntensity,
  PointCloudStylingPoint,
  PointCloudStylingPointShape,
  PointCloudStylingType,
  PointColorType,
  PointShape,
} from '@trimble/types';
import { getPointCloudElevationGradient } from '@trimble/utilities';
import {
  PotreeManagerModel,
  PotreeManagerPlugin,
} from '@technology/web3d-plugin-potreemanager';
import { Color, Vector2 } from '@technology/web3d';
import { cssToWeb3dColor } from '@trimble/web3d-utils';

/**
 * Utility class for rendering point cloud styling configurations to 3D models and viewers.
 * This class handles the conversion between UI styling configurations and the underlying
 * 3D engine settings for point cloud visualization.
 */
export class PointCloudStylingRenderer {
  private readonly stylingTypeMap: Record<
    PointCloudStylingType,
    (typeof PointColorType)[keyof typeof PointColorType]
  > = {
    [PointCloudStylingType.Classification]: PointColorType.CLASSIFICATION,
    [PointCloudStylingType.Clearance]: PointColorType.CLEARANCE,
    [PointCloudStylingType.Elevation]: PointColorType.ELEVATION,
    [PointCloudStylingType.Intensity]: PointColorType.INTENSITY,
    [PointCloudStylingType.Rgb]: PointColorType.RGB,
  };

  private readonly pointShapeMap: Record<
    PointCloudStylingPointShape,
    (typeof PointShape)[keyof typeof PointShape]
  > = {
    [PointCloudStylingPointShape.Circle]: PointShape.CIRCLE,
    [PointCloudStylingPointShape.Square]: PointShape.SQUARE,
    [PointCloudStylingPointShape.Paraboloide]: PointShape.PARABOLOID,
  };

  constructor(private readonly potreeManager: PotreeManagerPlugin) {}

  /**
   * Applies point cloud styling to a specific model.
   * @param model - The PotreeManagerModel to apply styling to
   * @param styling - The point cloud styling configuration
   */
  public applyModelStyling(
    model: PotreeManagerModel,
    styling: PointCloudStyling
  ): void {
    this.applyStylingType(model, styling);
    this.applyClassification(model, styling.classification);
    this.applyClearance(model, styling.clearance);
    this.applyElevation(model, styling.elevation);
    this.applyIntensity(model, styling.intensity);
    this.applyPoint(model, styling.point);
  }

  /**
   * Applies global styling settings to the PotreeManager plugin.
   * @param styling - The point cloud styling configuration
   */
  public applyGlobalStyling(styling: PointCloudStyling): void {
    this.applyEyeDomeLighting(this.potreeManager, styling.eyeDomeLighting);
  }

  private applyStylingType(
    model: PotreeManagerModel,
    styling: PointCloudStyling
  ): void {
    const stylingType = styling.stylingType ?? PointCloudStylingType.Rgb;

    // Handle special case for classification with intensity
    if (
      stylingType === PointCloudStylingType.Classification &&
      styling.intensity?.blendIntensity
    ) {
      model.pointColorType = PointColorType.INTENSITY_CLASSIFICATION;
      return;
    }

    // Handle special case for RGB with blended intensity
    if (
      stylingType === PointCloudStylingType.Rgb &&
      styling.intensity?.blendIntensity
    ) {
      model.pointColorType = PointColorType.INTENSITY;
      return;
    }

    // Use lookup map for standard styling types
    model.pointColorType = this.stylingTypeMap[stylingType];
  }

  private applyClassification(
    model: PotreeManagerModel,
    classification?: PointCloudStylingClassification
  ): void {
    if (
      !classification?.classDefinitions ||
      classification.classDefinitions.length === 0
    )
      return;

    // We need to fill the gaps in the classifications array for web3d
    const classDefinitions = this.fillGapsInClassDefinitions(
      classification.classDefinitions
    );

    model.setClassificationColors(
      classDefinitions.map((def) => cssToWeb3dColor(def.color))
    );
    model.setClassificationVisibility(
      classDefinitions.map((def) => def.visibility)
    );
  }

  private fillGapsInClassDefinitions(
    classDefinitions: PointCloudClassDefinition[]
  ): PointCloudClassDefinition[] {
    return Array.from({ length: MAX_CLASSIFICATION_ID + 1 }, (_, i) => {
      return (
        classDefinitions.find((c) => c.id === i) ?? {
          id: i,
          visibility: false,
          // Color is required, but it won't be used because the visibility is false.
          // Using arbitrary color in the format RRGGBB to avoid errors.
          color: '000000',
        }
      );
    });
  }

  private applyClearance(
    model: PotreeManagerModel,
    clearance?: PointCloudStylingClearance
  ): void {
    if (!clearance) return;

    if (clearance.bands) {
      const bandBoundaries = clearance.bands.map((band) => band.start);
      const bandColors = clearance.bands
        .slice(0, -1)
        .map((band) => cssToWeb3dColor(band.color));

      model.setClearanceBands(bandBoundaries, bandColors);
    }

    if (clearance.defaultColor !== undefined) {
      model.defaultClearanceColor = cssToWeb3dColor(clearance.defaultColor);
    }

    if (clearance.defaultTransparent !== undefined) {
      model.defaultClearanceTransparent = clearance.defaultTransparent;
    }
  }

  private applyElevation(
    model: PotreeManagerModel,
    elevation?: PointCloudStylingElevation
  ): void {
    if (!elevation) return;

    const reference = elevation.reference ?? 0;
    const offset = elevation.offset ?? 0;
    const bandCount = elevation.bandCount ?? 1;
    const tolerance = elevation.tolerance ?? 1;

    const toleranceWithExtraBand = (tolerance / bandCount) * (bandCount + 1);

    model.elevationRange = new Vector2(
      reference + offset - toleranceWithExtraBand,
      reference + offset + toleranceWithExtraBand
    );

    const gradient = getPointCloudElevationGradient(bandCount + 1);
    model.setElevationColors(
      gradient.map(([value, color]) => [value, cssToWeb3dColor(color)])
    );
  }

  private applyEyeDomeLighting(
    potreeManager: PotreeManagerPlugin,
    eyeDomeLighting?: PointCloudStylingEyeDomeLighting
  ): void {
    if (!eyeDomeLighting) return;

    if (eyeDomeLighting.enabled !== undefined) {
      potreeManager.eyeDomeLightingEnabled = eyeDomeLighting.enabled;
    }

    if (eyeDomeLighting.radius !== undefined) {
      potreeManager.eyeDomeLightingRadius = eyeDomeLighting.radius;
    }

    if (eyeDomeLighting.strength !== undefined) {
      potreeManager.eyeDomeLightingStrength = eyeDomeLighting.strength;
    }
  }

  private applyIntensity(
    model: PotreeManagerModel,
    intensity?: PointCloudStylingIntensity
  ): void {
    if (!intensity) return;

    if (intensity.gradient) {
      const gradient: [number, Color][] = [];

      gradient.push([0, cssToWeb3dColor(intensity.gradient.startColor)]);

      gradient.push([
        intensity.gradient.offset,
        cssToWeb3dColor(intensity.gradient.endColor),
      ]);

      model.setIntensityColors(gradient);
    }
  }

  private applyPoint(
    model: PotreeManagerModel,
    point?: PointCloudStylingPoint
  ): void {
    if (!point) return;

    if (point.budget !== undefined) {
      model.pointBudget = point.budget;
    }

    if (point.densityBias !== undefined) {
      model.pointDensityBias = point.densityBias;
    }

    if (point.size !== undefined) {
      model.pointSize = point.size;
    }

    if (point.shape !== undefined) {
      model.pointShape = this.pointShapeMap[point.shape];
    }

    if (point.sizeAttenuation !== undefined) {
      model.sizeAttenuation = point.sizeAttenuation;
    }
  }
}
